import React, { useEffect, useState } from "react";
import { ArrowLeft, BarChart3, ShoppingCart, Store, TrendingUp, WalletCards } from "lucide-react";
import { Link } from "react-router-dom";
import { getPublicBusinessResults } from "@/services/businessService";
import Logo from "@/components/boost/Logo";

const money = (value) => `$${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const number = (value) => Number(value || 0).toLocaleString("en-US");

export default function BusinessResults() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getPublicBusinessResults()
      .then(setData)
      .catch((err) => setError(err.message || "Unable to load results."))
      .finally(() => setLoading(false));
  }, []);

  const cards = data ? [
    ["Total Stores", number(data.total_stores), Store],
    ["Total Revenue", money(data.total_revenue), WalletCards],
    ["Total Orders", number(data.total_orders), ShoppingCart],
    ["Estimated Profit", money(data.total_estimated_profit), TrendingUp],
    ["Products Sold", number(data.total_products_sold), BarChart3],
    ["Profit Margin", `${Number(data.overall_profit_margin || 0).toFixed(1)}%`, TrendingUp],
    ["Average Order Value", money(data.average_order_value), WalletCards],
    ["Total Refunds", money(data.total_refunds), WalletCards],
  ] : [];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur sticky top-0 z-40">
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/" aria-label="Back to BoostF home"><Logo /></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Back to BoostF
          </Link>
        </div>
      </header>

      <main>
        <section className="aurora-bg relative overflow-hidden">
          <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
          <div className="container-x py-20 md:py-28">
            <div className="max-w-3xl">
              <div className="chip">BoostF Business Results</div>
              <h1 className="mt-5 font-display text-5xl md:text-7xl font-black tracking-[-0.04em] leading-[0.95]">
                Real business performance, <span className="gradient-text-emerald">shown collectively</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed">
                Aggregate performance from stores tracked through the BoostF business ecosystem. Individual store names and private business figures are never shown on this public page.
              </p>
            </div>
          </div>
        </section>

        <section className="section-pad">
          <div className="container-x">
            {loading && <div className="py-20 text-center text-slate-500">Loading business results…</div>}
            {error && !loading && <div className="py-20 text-center text-red-600">{error}</div>}
            {!loading && !error && data && data.total_stores === 0 && (
              <div className="rounded-3xl border border-slate-100 bg-slate-50 p-10 text-center shadow-premium">
                <h2 className="font-display text-2xl font-bold">Business results are being prepared.</h2>
                <p className="mt-2 text-slate-600">No public business data is available yet.</p>
              </div>
            )}
            {!loading && !error && data && data.total_stores > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map(([label, value, Icon], index) => (
                  <div key={label} className={`rounded-3xl p-6 border shadow-premium ${index % 3 === 1 ? "bg-[#0f172a] text-white border-white/10" : "bg-white border-slate-100"}`}>
                    <div className={`w-10 h-10 rounded-2xl grid place-items-center ${index % 3 === 1 ? "bg-emerald-500/15 text-lime-300" : "bg-emerald-50 text-emerald-700"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className={`mt-5 text-[11px] font-mono uppercase tracking-widest ${index % 3 === 1 ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
                    <div className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight">{value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
